<script setup>
import Boxes from '@/mini/components/Boxes.vue'
import Box from '@/mini/components/Box.vue'
import Button from '@/mini/components/Button.vue'
import Sep from '@/mini/components/Sep.vue'
import { watch } from 'vue'
import { getFetchOptionLabel, getCandidateImage } from '@/utils/cardFormUtils'
import Space from '@/mini/components/Space.vue'

const props = defineProps({
  form: { type: Object, required: true },
  composedCardId: { type: String, default: '' },
  isFetchingData: { type: Boolean, default: false },
  fetchCandidates: { type: Array, default: () => [] },
  selectedFetchIndex: { type: Number, default: 0 },
  idPrefix: { type: String, default: '' },
  mode: { type: String, default: 'create' }, // 'create' | 'edit'
  availableGroups: { type: Array, default: () => [] },
})

const emit = defineEmits(['fetch', 'import', 'update:selectedFetchIndex'])

function applyGroupDefaults(entry) {
  const group = props.availableGroups.find(g => g.name === entry.group_name)
  if (!group) return
  if (group.default_storage_folder && !entry.folder) entry.folder = group.default_storage_folder
  if (group.default_storage_page && !entry.page) entry.page = group.default_storage_page
}

function removeEntry(index, entry) {
  if (entry.id && props.form.group_entries_deleted) {
    props.form.group_entries_deleted.push(entry.id)
  }
  props.form.group_entries.splice(index, 1)
}

watch(() => props.form.to_buy, (val) => {
  if (val) {
    (props.form.group_entries ?? []).forEach(e => { e.quantity = 0 })
    props.form.sell_to_sell = false
  } else {
    props.form.to_follow = false
    if ((props.form.group_entries ?? []).every(e => (Number(e.quantity) || 0) === 0)) {
      if (props.form.group_entries?.length) props.form.group_entries[0].quantity = 1
    }
  }
})

watch(() => props.form.sell_to_sell, (val) => {
  if (val) props.form.to_buy = false
})
</script>

<template>
  <Box size="100" padding="0">
    <Boxes gap="0">
      <Box size="100">
        <h3 class="p m-0 bold">Card info</h3>
      </Box>
      <Box class="box-zero-33 box-sm-20">
        <label class="S" :for="`${idPrefix}set-type`">Set type</label>
        <select :id="`${idPrefix}set-type`" v-model="form.set_type" required>
          <option value="ST">ST</option>
          <option value="P">P</option>
          <option value="OP">OP</option>
          <option value="EB">EB</option>
          <option value="PRB">PRB</option>
        </select>
      </Box>
      <Box class="box-zero-33 box-sm-20">
        <label class="S" :for="`${idPrefix}set-id`">Set ID</label>
        <input :id="`${idPrefix}set-id`" v-model="form.set_id" type="text" />
      </Box>
      <Box class="box-zero-33 box-sm-20">
        <label class="S" :for="`${idPrefix}card-number`">Card number</label>
        <input :id="`${idPrefix}card-number`" v-model="form.card_number" type="text" />
      </Box>
      <Box v-if="mode === 'edit'" size="40">
        <label class="S" :for="`${idPrefix}card-id`">Card ID</label>
        <input :id="`${idPrefix}card-id`" v-model="form.card_id" type="text" />
      </Box>
      <Box size="100">
        <Button size="" color="bad" class="m-0 round px-15" @click="emit('fetch')">
          {{ isFetchingData ? 'Fetching...' : 'Fetch ' + composedCardId + ' data on OPTCG API' }}
        </Button>
      </Box>
      <Box v-if="fetchCandidates.length > 1" size="100" padding="2" class="border bad-border b-rad-10 bad-bubble-top-left my-15">
        <p class="label S mb-0">Select card to import</p>
        <Boxes class="pt-1 pb-2">
          <Box
            v-for="(candidate, index) in fetchCandidates"
            :key="`${idPrefix}candidate-${index}`"
            :class="['fw-bg b-rad-10', selectedFetchIndex === index ? 'bk-bg' : '', 'box-zero-50 box-sm-25']"
            @click="emit('update:selectedFetchIndex', index)"
            style="cursor: pointer;"
          >
            <img v-if="getCandidateImage(candidate)" :src="getCandidateImage(candidate)" class="img mb-1" />
            <p class="S m-0" :class="selectedFetchIndex === index ? 'wh-text' : ''">{{ getFetchOptionLabel(candidate, index) }}</p>
          </Box>
        </Boxes>
        <Button color="bad" class="m-0 round px-15" @click="emit('import')">Import selected card</Button>
      </Box>
    </Boxes>
  </Box>
  <Box size="100" padding="0">
    <Boxes gap="0">
      <Box v-if="form.card_image" size="30">
        <img :src="form.card_image" :alt="form.card_name" class="card-image img" style="width: 100%; height: auto;" />
      </Box>
      <Box :size="form.card_image ? 70 : 100" class="px-0">
        <Boxes gap="0">
          <Box size="100">
            <label class="S" :for="`${idPrefix}card-name`">Card name *</label>
            <input :id="`${idPrefix}card-name`" v-model="form.card_name" type="text" required />
          </Box>
          <Box class="box-zero-33">
            <label class="S" :for="`${idPrefix}card-color`">Color</label>
            <input :id="`${idPrefix}card-color`" v-model="form.card_color" type="text" readonly />
          </Box>
          <Box class="box-zero-33">
            <label class="S" :for="`${idPrefix}card-type`">Type</label>
            <input :id="`${idPrefix}card-type`" v-model="form.card_type" type="text" readonly />
          </Box>
          <Box class="box-zero-33">
            <label class="S" :for="`${idPrefix}rarity`">Rarity</label>
            <input :id="`${idPrefix}rarity`" v-model="form.rarity" type="text" readonly />
          </Box>
          <Box size="100">
            <label class="S" :for="`${idPrefix}card-image`">Card image URL</label>
            <input :id="`${idPrefix}card-image`" v-model="form.card_image" type="url" class="S" />
          </Box>
        </Boxes>
      </Box>
    </Boxes>

  </Box>
  <Box size="100" padding="0">
    <Boxes gap="0">
      <Box size="100">
        <h3 class="p m-0 bold">Card management</h3>
      </Box>
      <Box size="100" padding="0" v-for="(entry, index) in (form.group_entries ?? [])" :key="index" class="box-shadow p-15 b-rad-10 mb-2">
        <Boxes gap="0">
          <Box class="box-zero-50 box-sm-20">
            <label class="S">Group</label>
            <input v-model="entry.group_name" type="text" placeholder="e.g. OP01" @change="applyGroupDefaults(entry)" />
          </Box>
          <Box class="box-zero-50 box-sm-20">
            <label class="S">Qty</label>
            <input v-model.number="entry.quantity" type="number" min="0" />
          </Box>
          <Box v-if="!entry.warehouse" class="box-zero-33 box-sm-20">
            <label class="S">Folder</label>
            <input v-model="entry.folder" type="text" />
          </Box>
          <Box v-if="!entry.warehouse" class="box-zero-33 box-sm-20">
            <label class="S">Page</label>
            <input v-model="entry.page" type="text" />
          </Box>
          <Box class="box-zero-33 box-sm-20">
            <label class="S">CT Wh</label>
            <input v-model="entry.warehouse" type="checkbox" />
          </Box>
          <Box size="100" class="">
            <Button v-if="(form.group_entries ?? []).length > 1" size="XS" color="danger" invert class="round px-1" @click="removeEntry(index, entry)">✕ Remove</Button>
          </Box>
        </Boxes>
      </Box>
      <Box size="100" class="py-0">
        <Button size="XS" color="second-color" invert class="round px-1"
          @click="(form.group_entries ?? (form.group_entries = [])).push({ id: null, group_name: '', quantity: 1, folder: '', page: '', warehouse: false })">
          + Add storage
        </Button>
      </Box>
      <Box v-if="false" class="box-zero-50 box-sm-16">
        <label class="S" :for="`${idPrefix}fruit`">Fruit</label>
        <input :id="`${idPrefix}fruit`" v-model="form.fruit" type="text" />
      </Box>
      <Space/>
      <Box class="box-zero-50 box-sm-16">
        <label class="S" :for="`${idPrefix}foil`">Foil</label>
        <input :id="`${idPrefix}foil`" v-model="form.foil" type="checkbox" true-value="TRUE" false-value="FALSE" />
      </Box>
      <Box v-if="!form.to_buy" class="box-zero-50 box-sm-16">
        <label class="S" :for="`${idPrefix}favourite`">Favourite</label>
        <input :id="`${idPrefix}favourite`" v-model="form.favourite" type="checkbox" />
      </Box>
      <Box v-else class="box-zero-50 box-sm-16">
        <label class="S" :for="`${idPrefix}to-follow`">Follow price</label>
        <input :id="`${idPrefix}to-follow`" v-model="form.to_follow" type="checkbox" />
      </Box>
    </Boxes>
  </Box>
  <Sep class="my-05"/>
  <Box size="100" padding="0">
    <Boxes class="gy-0">
      <Box size="100">
        <h3 class="p m-0 bold">Card market</h3>
      </Box>
      <Box class="box-zero-50 box-sm-20">
        <label class="S" :for="`${idPrefix}sell-to-sell`">To sell</label>
        <input :id="`${idPrefix}sell-to-sell`" v-model="form.sell_to_sell" type="checkbox" />
      </Box>
      <Box class="box-zero-50 box-sm-20">
        <label class="S" :for="`${idPrefix}to-buy`">Wishlist</label>
        <input :id="`${idPrefix}to-buy`" v-model="form.to_buy" type="checkbox" />
      </Box>
      <Box v-if="form.sell_to_sell" size="100" background="fw" class="b-rad-5 fw-bubble-top-left mt-1">
        <Boxes>
          <Box class="box-zero-33">
            <label class="S" :for="`${idPrefix}sell-quantity`">Qty to sell</label>
            <input :id="`${idPrefix}sell-quantity`" v-model.number="form.sell_quantity_to_sell" type="number" min="1" />
          </Box>
          <Box class="box-zero-33">
            <label class="S" :for="`${idPrefix}sell-folder`">Sell folder</label>
            <input :id="`${idPrefix}sell-folder`" v-model="form.sell_folder" type="text" />
          </Box>
          <Box class="box-zero-33">
            <label class="S" :for="`${idPrefix}sell-page`">Sell page</label>
            <input :id="`${idPrefix}sell-page`" v-model="form.sell_page" type="text" />
          </Box>
        </Boxes>
      </Box>
      <Box size="100" padding="0">
        <Boxes gap="0">
          <Box class="box-zero-33 box-md-16">
            <label class="S" :for="`${idPrefix}inventory-price`">Inventory</label>
            <input
              :id="`${idPrefix}inventory-price`"
              v-model="form.inventory_price"
              type="number" step="0.01" min="0"
            />
          </Box>
          <Box class="box-zero-33 box-md-16">
            <label class="S" :for="`${idPrefix}market-price`">Market price</label>
            <input
              :id="`${idPrefix}market-price`"
              v-model="form.market_price"
              type="number" step="0.01" min="0"
            />
          </Box>
          <Box class="box-zero-33 box-md-16">
            <label class="S danger-text" :for="`${idPrefix}real-price`">Real value</label>
            <input :id="`${idPrefix}real-price`" v-model="form.real_price" type="number" step="0.01" min="0" />
          </Box>
          <Box class="box-zero-33 box-md-16">
            <label class="S blue-text" :for="`${idPrefix}ct-market-price`">CT (min) price</label>
            <input
              :id="`${idPrefix}ct-market-price`"
              :value="form.cardtrader_market_price != null ? Number(form.cardtrader_market_price).toFixed(2) : ''"
              type="number" step="0.01" min="0"
              readonly
            />
          </Box>
          <Box class="box-zero-33 box-md-16">
            <label class="S blue-text" :for="`${idPrefix}ct-lowest-price`">CT lowest</label>
            <input
              :id="`${idPrefix}ct-lowest-price`"
              :value="form.cardtrader_lowest_market_price != null ? Number(form.cardtrader_lowest_market_price).toFixed(2) : ''"
              type="number" step="0.01" min="0"
              readonly
            />
          </Box>
          <Box class="box-zero-33 box-md-16">
            <label class="S success-text" :for="`${idPrefix}purchase-price`">Purchased at</label>
            <input :id="`${idPrefix}purchase-price`" v-model="form.purchase_price" type="number" step="0.01" min="0" />
          </Box>
        </Boxes>
      </Box>
    </Boxes>
  </Box>
</template>
